import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Bird,
  Filter,
  ChevronRight,
  ClipboardCheck,
  Coins,
  Lock,
  HeartPulse,
  Package,
  Plus,
  Truck,
  Warehouse,
  Weight,
  X,
} from 'lucide-react';

import { useCamadaDetalle, useCamadasListado } from '@/lib/queries/camadas';
import { useSalidasCamada } from '@/lib/queries/salidas';
import { useEntityId } from '@/lib/offline/operations';
import { fmtFecha, fmtInt } from '@/lib/format';
import { formatoDecimal } from '@/lib/liquidacion';
import { extractApiMessage } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { ModalOverlay } from '@/components/ModalOverlay';
import { CamadaManagement } from '@/components/CamadaManagement';
import './CamadasPage.css';

export function CamadasPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const requestedId = useEntityId(params.get('camada'), 'camadas');
  const [year, setYear] = useState<number | undefined>();
  const [sexo, setSexo] = useState<'machos' | 'hembras' | undefined>();
  const [offset, setOffset] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const q = useCamadasListado({ year, sexo, offset });
  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const defaultId = items.find((c) => !c.cerrada)?.id ?? items[0]?.id ?? null;
  const selectedId = params.has('camada') ? requestedId : defaultId;
  const detalleQ = useCamadaDetalle(selectedId);
  const detalle = detalleQ.data;
  const camada = detalle?.camada;
  const kpis = detalle?.kpis;
  const salidasQ = useSalidasCamada(camada?.cerrada ? camada.id : null);
  const salidas = salidasQ.data?.filter((s) => !s.anulada);
  const user = useAuthStore((s) => s.user);
  const canSeeFinance = user?.rol !== 'operario';
  const canManage = user?.rol === 'admin' || user?.rol === 'ganadero';
  const hayFiltros = year != null || sexo != null;
  const today = format(new Date(), 'yyyy-MM-dd');
  const invalidSelection = params.has('camada') && requestedId == null;

  useEffect(() => {
    if (camada && params.get('camada') !== String(camada.id)) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('camada', String(camada.id));
          return next;
        },
        { replace: true },
      );
    }
  }, [camada, params, setParams]);

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => {
    const values = new Set([currentYear, currentYear - 1, currentYear - 2]);
    items.forEach((c) => values.add(Number(c.fecha_entrada.slice(0, 4))));
    if (year != null) values.add(year);
    return [...values].sort((a, b) => b - a);
  }, [items, currentYear, year]);
  const options = camada && !items.some((c) => c.id === camada.id) ? [camada, ...items] : items;

  function selectCamada(value: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('camada', value);
      else next.delete('camada');
      return next;
    });
  }

  function resetSelection() {
    setOffset(0);
    selectCamada('');
  }

  const salidasTotal = salidas?.reduce((sum, s) => sum + s.unidades_cargadas_total, 0);
  const metrics =
    kpis && camada
      ? [
          {
            Icon: Bird,
            label: camada.cerrada ? 'Aves salidas' : 'Aves vivas',
            value: camada.cerrada
              ? salidasTotal == null
                ? '—'
                : fmtInt(salidasTotal)
              : fmtInt(kpis.aves_actuales),
          },
          {
            Icon: HeartPulse,
            label: 'Mortalidad',
            value: kpis.aves_iniciales > 0 ? formatoDecimal(kpis.mortalidad_pct, 1) : '—',
            unit: '%',
          },
          {
            Icon: Weight,
            label: camada.cerrada ? 'Peso salida' : 'Último peso',
            value: kpis.peso_medio_kg == null ? '—' : formatoDecimal(kpis.peso_medio_kg, 2),
            unit: 'kg',
          },
          {
            Icon: Package,
            label: 'Pienso',
            value: formatoDecimal(kpis.pienso_total_kg / 1000, 1),
            unit: 't',
          },
        ]
      : [];

  return (
    <main className="camadas-page container mx-auto max-w-md px-4">
      <header className="camadas-heading">
        <h1>Camadas</h1>
        <select
          className="camadas-selector"
          aria-label="Seleccionar camada"
          value={selectedId ?? ''}
          disabled={!options.length}
          onChange={(e) => selectCamada(e.target.value)}
        >
          {!options.some((c) => c.id === selectedId) && (
            <option value={selectedId ?? ''}>Seleccionar camada</option>
          )}
          {options.map((c) => (
            <option key={c.id} value={c.id}>
              {c.codigo_lote}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="camadas-filter-button"
          aria-label={`Filtrar historial${hayFiltros ? ' · Filtros activos' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen(true)}
        >
          <Filter size={22} aria-hidden />
          {hayFiltros && <span className="camadas-filter-dot" aria-hidden />}
        </button>
        {canManage && (
          <Link
            to="/camadas/nueva"
            className="ui-add-button"
            aria-label="Nueva camada"
            data-testid="camadas-cta-crear"
          >
            <Plus size={22} aria-hidden />
          </Link>
        )}
      </header>

      {filtersOpen &&
        createPortal(
          <ModalOverlay
            placement="bottom"
            className="max-w-md text-text-primary"
            onBackdrop={() => setFiltersOpen(false)}
            ariaLabelledby="camadas-filter-title"
          >
            <header className="mb-4 flex items-center justify-between gap-3">
              <h2 id="camadas-filter-title" className="ui-page-title">
                Filtrar historial
              </h2>
              <button
                type="button"
                className="ui-icon-button ui-close-button"
                aria-label="Cerrar filtros"
                onClick={() => setFiltersOpen(false)}
              >
                <X size={22} aria-hidden />
              </button>
            </header>
            <div className="camadas-filter-fields">
              <label>
                {t('camadas.filtro_anio')}
                <select
                  aria-label={t('camadas.filtro_anio')}
                  value={year ?? ''}
                  data-testid="camadas-filtro-anio"
                  onChange={(e) => {
                    setYear(e.target.value ? Number(e.target.value) : undefined);
                    resetSelection();
                  }}
                >
                  <option value="">Todos los años</option>
                  {years.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t('camadas.filtro_sexo')}
                <select
                  aria-label={t('camadas.filtro_sexo')}
                  value={sexo ?? ''}
                  data-testid="camadas-filtro-sexo"
                  onChange={(e) => {
                    setSexo((e.target.value || undefined) as typeof sexo);
                    resetSelection();
                  }}
                >
                  <option value="">{t('camadas.filtro_sexo_todos')}</option>
                  <option value="machos">{t('camadas.filtro_sexo_machos')}</option>
                  <option value="hembras">{t('camadas.filtro_sexo_hembras')}</option>
                </select>
              </label>
              {hayFiltros && (
                <button
                  type="button"
                  className="ui-secondary"
                  data-testid="camadas-filtro-limpiar"
                  onClick={() => {
                    setYear(undefined);
                    setSexo(undefined);
                    resetSelection();
                  }}
                >
                  {t('camadas.filtro_limpiar')}
                </button>
              )}
            </div>
            <button
              type="button"
              className="ui-primary mt-4 w-full"
              onClick={() => setFiltersOpen(false)}
            >
              Ver camadas
            </button>
          </ModalOverlay>,
          document.body,
        )}

      {q.data && q.data.total > 50 && (
        <nav className="camadas-pagination" aria-label="Historial de camadas">
          <button
            type="button"
            disabled={offset === 0 || q.isFetching}
            onClick={() => {
              selectCamada('');
              setOffset(Math.max(offset - 50, 0));
            }}
          >
            Camadas más recientes
          </button>
          <span>
            {offset + 1}–{Math.min(offset + 50, q.data.total)} / {q.data.total}
          </span>
          <button
            type="button"
            disabled={offset + 50 >= q.data.total || q.isFetching}
            onClick={() => {
              selectCamada('');
              setOffset(offset + 50);
            }}
          >
            Camadas anteriores
          </button>
        </nav>
      )}
      {(q.isLoading || detalleQ.isLoading) && <p role="status">{t('common.loading')}</p>}
      {q.isError && (
        <p role="alert">No se pudo cargar el historial de camadas. {extractApiMessage(q.error)}</p>
      )}
      {(invalidSelection || detalleQ.isError) && (
        <p role="alert">
          No se pudo cargar la camada seleccionada.
          {detalleQ.error ? ` ${extractApiMessage(detalleQ.error)}` : ''}
        </p>
      )}
      {q.isSuccess && !items.length && !params.has('camada') && (
        <div className="camadas-empty" data-testid="camadas-empty-state">
          <Warehouse size={36} aria-hidden />
          <p>
            {hayFiltros ? 'No hay camadas con estos filtros.' : t('camadas.estado_vacia_titulo')}
          </p>
          {!hayFiltros && canManage && (
            <Link className="ui-primary" to="/camadas/nueva">
              {t('camadas.estado_vacia_cta')}
            </Link>
          )}
        </div>
      )}

      {camada && kpis && detalle && (
        <div className="camadas-sections">
          <section className="home-hero" aria-label={`Datos de la camada ${camada.codigo_lote}`}>
            <div className="camadas-hero-heading">
              <h2 className="home-hero-title">
                <span className="home-hero-name" title={camada.codigo_lote}>
                  Camada {camada.codigo_lote}
                </span>
              </h2>
              <CamadaManagement
                key={`${camada.id}:${user?.id}:${user?.rol}`}
                detalle={detalle}
                canManage={canManage}
                canSeeFinance={canSeeFinance}
              />
            </div>
            <div className="camadas-meta">
              <p className="home-day">
                {camada.cerrada
                  ? `${kpis.dias_engorde} días`
                  : kpis.dias_engorde < 0
                    ? 'Inicio previsto'
                    : `Día ${kpis.dias_engorde + 1} de engorde`}{' '}
                · {camada.sexo === 'machos' ? 'Machos' : 'Hembras'}
              </p>
              <span className="camadas-status">{camada.cerrada ? 'Finalizada' : 'En curso'}</span>
            </div>
            <dl className="home-kpis">
              {metrics.map(({ Icon, label, value, unit }) => (
                <div key={label}>
                  <dt>
                    <span className="home-kpi-icon">
                      <Icon size={18} strokeWidth={1.8} aria-hidden />
                    </span>
                    <span>{label}</span>
                  </dt>
                  <dd>
                    {value}
                    {unit && value !== '—' && (
                      <>
                        {' '}
                        <small>{unit}</small>
                      </>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="camadas-date">
              {camada.cerrada
                ? `${fmtFecha(camada.fecha_entrada)} · ${fmtFecha(camada.fecha_cierre)}`
                : `Entrada · ${fmtFecha(camada.fecha_entrada)}`}
            </p>
          </section>

          <section aria-labelledby="camadas-naves-title">
            <div className="home-section-heading">
              <h2 id="camadas-naves-title">Naves de la camada</h2>
              <span>{detalle.lotes.length} naves</span>
            </div>
            {detalle.lotes.length ? (
              <ul className="camadas-naves">
                {detalle.lotes.map((lote) => {
                  const salidasNave = salidas
                    ?.flatMap((s) => s.lineas)
                    .filter((linea) => linea.lote_nave_id === lote.lote_id)
                    .reduce((sum, linea) => sum + linea.unidades_cargadas, 0);
                  return (
                    <li key={lote.lote_id}>
                      <Link to={`/naves/${lote.nave_id}?camada=${camada.id}`}>
                        <span className="camadas-nave-icon">
                          <Warehouse size={22} strokeWidth={1.8} aria-hidden />
                        </span>
                        <strong className="camadas-nave-name">{lote.nave_codigo}</strong>
                        <span className="camadas-nave-value">
                          <strong>
                            {camada.cerrada
                              ? salidasNave == null
                                ? '—'
                                : fmtInt(salidasNave)
                              : fmtInt(lote.aves_actuales)}
                          </strong>
                          <small>{camada.cerrada ? 'salidas' : 'aves vivas'}</small>
                        </span>
                        <ChevronRight size={18} aria-hidden />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div>
                <p className="camadas-help">Esta camada todavía no tiene naves asignadas.</p>
                {canManage && !camada.cerrada && (
                  <Link
                    className="ui-secondary mt-3 w-full"
                    to={`/camadas/${camada.id}/asignar-lotes`}
                  >
                    <Warehouse size={20} aria-hidden />
                    Asignar naves
                  </Link>
                )}
              </div>
            )}
          </section>

          {!camada.cerrada && canManage && (
            <Link
              className="ui-primary camadas-part"
              to={`/camadas/${camada.id}/registro-diario?fecha=${today}`}
              state={{ backgroundLocation: location }}
            >
              <ClipboardCheck size={20} aria-hidden />
              <span>Registrar parte de hoy</span>
              <ChevronRight size={18} aria-hidden />
            </Link>
          )}
          <div className="camadas-links">
            {canManage && (
              <Link to={`/camadas/${camada.id}/cerrar`}>
                <Lock size={20} aria-hidden />
                {camada.cerrada ? 'Cierre y liquidación' : 'Cerrar camada'}
              </Link>
            )}
            <Link to={`/camadas/${camada.id}/partes`}>
              <ClipboardCheck size={20} aria-hidden />
              Partes diarios
            </Link>
            <Link to={`/camadas/${camada.id}/salidas`}>
              <Truck size={20} aria-hidden />
              Salidas a matadero
            </Link>
            <Link to={`/camadas/${camada.id}/entregas-pienso`}>
              <Package size={20} aria-hidden />
              Pienso
            </Link>
            <Link to={`/camadas/${camada.id}/entradas-animales`}>
              <Truck size={20} aria-hidden />
              Llegadas de camiones
            </Link>
            {canSeeFinance && (
              <Link
                to={
                  camada.cerrada
                    ? `/liquidacion/${camada.id}`
                    : `/liquidacion/${camada.id}/evolucion`
                }
              >
                <Coins size={20} aria-hidden />
                Liquidación
              </Link>
            )}
          </div>
          {salidasQ.isError && (
            <p role="status" className="camadas-help">
              Salidas no disponibles. Conecta para actualizar.
            </p>
          )}
          <p className="camadas-help">
            {camada.cerrada
              ? 'Peso medio de las salidas confirmadas.'
              : 'Peso medio de los últimos muestreos por nave.'}{' '}
            Pienso entregado acumulado.
          </p>
        </div>
      )}
    </main>
  );
}

export default CamadasPage;
